import React from 'react';

export const InstallAppModal = ({ isOpen, onClose, isIOS }) => {
  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl transition-all dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 p-2 dark:bg-blue-950/60">
              <img
                src="/campusguide-logo.webp"
                alt="CampusGuide logo"
                className="h-full w-full object-contain"
              />
            </div>
            <div>
              <h3 id="install-modal-title" className="text-lg font-black text-slate-950 dark:text-white">
                Install CampusGuide
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Fast, one-tap access to Bihar Cutoffs &amp; Predictors
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {isIOS ? (
            /* iOS Safari Step-by-Step Instructions */
            <div className="space-y-3 rounded-xl bg-slate-50 p-4 text-xs dark:bg-slate-850">
              <p className="font-bold text-slate-900 dark:text-white">
                To install on iPhone or iPad:
              </p>
              <ol className="space-y-2.5 text-slate-600 dark:text-slate-300">
                <li className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                    1
                  </span>
                  <span>
                    Tap the <strong>Share</strong> icon{' '}
                    <span className="inline-block rounded border border-slate-300 bg-white px-1 py-0.5 text-blue-600 dark:border-slate-700 dark:bg-slate-800">
                      <svg viewBox="0 0 24 24" className="inline h-3.5 w-3.5 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                        <polyline points="16 6 12 2 8 6" />
                        <line x1="12" y1="2" x2="12" y2="15" />
                      </svg>
                    </span>{' '}
                    in your Safari bottom navigation bar.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                    2
                  </span>
                  <span>
                    Scroll down and select <strong>&quot;Add to Home Screen&quot;</strong>{' '}
                    <span className="inline-block rounded border border-slate-300 bg-white px-1 py-0.5 font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                      [+]
                    </span>.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                    3
                  </span>
                  <span>
                    Tap <strong>&quot;Add&quot;</strong> in the top right corner of your screen.
                  </span>
                </li>
              </ol>
            </div>
          ) : (
            /* Desktop / Android Fallback Instructions */
            <div className="space-y-3 rounded-xl bg-slate-50 p-4 text-xs dark:bg-slate-850">
              <p className="font-bold text-slate-900 dark:text-white">
                How to install in your browser:
              </p>
              <ol className="space-y-2.5 text-slate-600 dark:text-slate-300">
                <li className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                    1
                  </span>
                  <span>
                    Look for the <strong>Install App</strong> icon{' '}
                    <span className="inline-block rounded border border-slate-300 bg-white px-1 py-0.5 text-blue-600 dark:border-slate-700 dark:bg-slate-800">
                      <svg viewBox="0 0 24 24" className="inline h-3.5 w-3.5 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect width="16" height="12" x="4" y="4" rx="2" />
                        <line x1="8" y1="20" x2="16" y2="20" />
                        <line x1="12" y1="16" x2="12" y2="20" />
                        <polyline points="15 9 12 12 9 9" />
                      </svg>
                    </span>{' '}
                    in your address bar (top right).
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                    2
                  </span>
                  <span>
                    Or open browser menu (<strong>&#8942;</strong> or <strong>&#8943;</strong>) and choose{' '}
                    <strong>&quot;Install CampusGuide&quot;</strong> or <strong>&quot;Add to Home screen&quot;</strong>.
                  </span>
                </li>
              </ol>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-center text-xs font-bold text-white shadow-sm transition hover:bg-blue-700"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallAppModal;
